import { useRef, useEffect } from 'react';
import gsap from 'gsap/all';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Image } from '@weareconceptstudio/core';

//* Styles
import PerfectLocationStyle from './style';

//* Lottie Files
import handMagnifier from '@/lottie/hand-magnify.json';

//* Component
import Editor from '@/components/Editor';
import TitleAnim from '@/modules/_animation/Title';
import LottieAnimation from '@/components/LottieAnimation';
import StyledQuotedText from '@/components/StyledQuotedText';

gsap.registerPlugin(ScrollTrigger);

const PerfectLocation = ({ description, items, title }) => {
	//! Refs
	const leftImageRef = useRef(null);
	const rightImageRef = useRef(null);
	const centerImageRef = useRef(null);
	const scrollTriggerRef = useRef(null);
	const titleRef = useRef(null);
	const descriptionRef = useRef(null);
	const reverseAnim = useRef(true);
	const styleRef = useRef();
	const triggerSection = useRef(null);

	function renderImagesBySide(items, side) {
		return items
			.filter((item) => item.additional_info.img_style === side)
			.map((item) => (
				<Image
					key={item.image.src}
					src={item.image.src}
					alt={item.image.alt}
				/>
			));
	}

	useEffect(() => {
		if (leftImageRef.current) {
			triggerSection.current = ScrollTrigger.create({
				start: 'top bottom',
				end: 'top top',
				trigger: titleRef.current,
				onEnter: () => {
					if (descriptionRef.current) {
						gsap.fromTo(descriptionRef.current, { opacity: 0 }, { opacity: 1, duration: 0.6, ease: 'power2.in', delay: 0.2 });
					}
				},
				onLeaveBack: () => {
					gsap.set(descriptionRef.current, { opacity: 0 });
					reverseAnim.current = true;

					gsap.set(leftImageRef.current, {
						opacity: 0,
						y: '100%',
						rotate: -15,
					});

					gsap.set([centerImageRef.current], {
						opacity: 0,
						y: '100%',
						rotate: 15,
					});
					gsap.set([rightImageRef.current], {
						opacity: 0,
						y: '100%',
						rotate: 15,
					});
				},
			});

			scrollTriggerRef.current = ScrollTrigger.create({
				trigger: rightImageRef.current,
				start: 'top-=100% 75%',
				end: 'top-=100% top',
				// markers: true,
				onEnter: () => {
					if (reverseAnim.current) {
						reverseAnim.current = false;

						gsap.fromTo(leftImageRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.in' });
						gsap.fromTo(
							leftImageRef.current,
							{
								y: '100%',
								rotate: -15,
							},
							{
								rotate: 0,
								y: '0%',
								duration: 1,
								ease: 'power2.out',
							}
						);
						gsap.fromTo([rightImageRef.current, centerImageRef.current], { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.in', stagger: 0.05 });

						gsap.fromTo(
							[rightImageRef.current],
							{
								y: '100%',
								rotate: 15,
							},
							{
								rotate: 0,
								y: '0%',
								duration: 1,
								delay: 0.15,
								ease: 'power2.out',
							}
						);
						gsap.fromTo(
							[centerImageRef.current],
							{
								y: '100%',
								rotate: 15,
							},
							{
								rotate: 0,
								y: '0%',
								duration: 1,
								delay: 0.2,
								ease: 'power2.out',
							}
						);
					}
				},
			});
		}

		return () => {
			if (scrollTriggerRef.current) {
				scrollTriggerRef.current.kill();
			}

			if (triggerSection.current) {
				triggerSection.current.kill();
			}
		};
	});

	return (
		<PerfectLocationStyle ref={styleRef}>
			<div className='outside-wrapper'>
				<div className='left-wrap'>
					<div
						ref={leftImageRef}
						className='wrap-outside-image'>
						{renderImagesBySide(items, 'left')}
					</div>
				</div>
				<div className='info-wrap'>
					<div
						ref={titleRef}
						className='title-wrap'>
						<TitleAnim
							trigger={'bottom'}
							load={true}>
							<StyledQuotedText text={title} />
						</TitleAnim>
					</div>
					<div
						ref={descriptionRef}
						className='description-wrap opacity-zero'>
						<Editor
							data={description}
							className={'align-center'}
						/>
					</div>
				</div>
				<div className='right-wrap'>
					<div
						ref={rightImageRef}
						className='wrap-outside-image'>
						{renderImagesBySide(items, 'right')}
					</div>
				</div>
				<div className='center-wrap'>
					<div className='lottie-outside-wrap wrap-desktop'>
						<LottieAnimation
							autoplay={false}
							isPlay={true}
							isReverse={false}
							loop={true}
							animData={handMagnifier}
							render={'svg'}
						/>
					</div>

					<div
						ref={centerImageRef}
						className='wrap-outside-image'>
						{renderImagesBySide(items, 'center')}
					</div>
				</div>
			</div>
		</PerfectLocationStyle>
	);
};

export default PerfectLocation;
